import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { colors } from 'bench-style-constants';
import { rgba } from 'color-helpers';

import { iconPaths } from './Icon';

const styleTypes = {
  primary: {
    backgroundColor: colors.water,
    backgroundColorHover: colors.creek,
    backgroundColorActive: colors.ocean,
    borderColor: colors.water,
    borderColorHover: colors.creek,
    borderColorActive: colors.ocean,
    color: colors.white,
    colorHover: colors.white,
    colorActive: colors.white,
    focusStyles: `
      border-color: ${colors.ocean};
      box-shadow: 0 0 4px 1px ${rgba(colors.creek, 0.65)}
    `,
  },
  secondary: {
    backgroundColor: colors.white,
    backgroundColorHover: colors.white,
    backgroundColorActive: colors.ocean,
    borderColor: colors.water,
    borderColorHover: colors.creek,
    borderColorActive: colors.ocean,
    color: colors.water,
    colorHover: colors.creek,
    colorActive: colors.white,
    focusStyles: `
      border-color: ${colors.water};
      box-shadow: 0 0 4px 1px ${rgba(colors.creek, 0.65)}
    `,
  },
  ghost: {
    backgroundColor: 'transparent',
    backgroundColorHover: 'transparent',
    backgroundColorActive: colors.ocean,
    borderColor: colors.stone,
    borderColorHover: colors.creek,
    borderColorActive: colors.ocean,
    color: colors.ash,
    colorHover: colors.creek,
    colorActive: colors.white,
    focusStyles: `
      border-color: ${colors.water};
      box-shadow: 0 0 4px 1px ${rgba(colors.creek, 0.65)}
    `,
  },
  'total-ghost': {
    backgroundColor: 'transparent',
    backgroundColorHover: 'transparent',
    backgroundColorActive: 'transparent',
    borderColor: 'transparent',
    borderColorHover: 'transparent',
    borderColorActive: 'transparent',
    color: colors.water,
    colorHover: colors.creek,
    colorActive: colors.ocean,
    focusStyles: `
      box-shadow: 0 0 4px 1px ${rgba(colors.creek, 0.65)}
    `,
  },
};

const sizes = {
  xsmall: {
    height: '16px',
    iconLeftPosition: '1px',
    iconSize: '12px',
    padding: '3px',
    paddingLeftWithIcon: '13px',
  },
  small: {
    height: '24px',
    iconLeftPosition: '2px',
    iconSize: '18px',
    padding: '4px',
    paddingLeftWithIcon: '20px',
  },
  medium: {
    height: '32px',
    iconLeftPosition: '6px',
    iconSize: '18px',
    padding: '12px',
    paddingLeftWithIcon: '28px',
  },
  large: {
    height: '40px',
    iconLeftPosition: '7px',
    iconSize: '24px',
    padding: '12px',
    paddingLeftWithIcon: '34px',
  },
};

const makeIconUrl = (color, icon) => {
  const path = `<path fill="${color}" d="${iconPaths[icon]}" />`;
  const svg = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">${path}</svg>`;
  const url = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  return `url('${url}')`;
};

const Button = styled.button`
  align-items: center;
  background-color: ${({ styleType }) => styleTypes[styleType].backgroundColor};
  ${({ icon, styleType }) =>
    !!icon && `background-image: ${makeIconUrl(styleTypes[styleType].color, icon)}`};
  background-position: ${({ size }) => sizes[size].iconLeftPosition} 50%;
  background-repeat: no-repeat;
  background-size: ${({ size }) => sizes[size].iconSize};
  border: 1px solid ${({ styleType }) => styleTypes[styleType].borderColor};
  border-radius: 2px;
  box-shadow: none;
  color: ${({ styleType }) => styleTypes[styleType].color};
  cursor: pointer;
  display: inline-flex;
  height: ${({ size }) => sizes[size].height};
  font-size: 12px;
  line-height: 1;
  justify-content: ${({ icon }) => (icon ? 'flex-start' : 'center')};
  min-width: ${({ size }) => sizes[size].height};
  padding: 0 ${({ size }) => sizes[size].padding};
  position: static;
  ${({ children, icon, size }) =>
    !!icon && !!children && `padding-left: ${sizes[size].paddingLeftWithIcon}`};
  text-decoration: none;
  text-transform: uppercase;
  transition-duration: 200ms;
  vertical-align: top;
  white-space: nowrap;

  &:focus {
    ${({ styleType }) => styleTypes[styleType].focusStyles};
    outline: none;
  }

  &:hover {
    background-color: ${({ styleType }) => styleTypes[styleType].backgroundColorHover};
    ${({ icon, styleType }) =>
      !!icon && `background-image: ${makeIconUrl(styleTypes[styleType].colorHover, icon)}`};
    border-color: ${({ styleType }) => styleTypes[styleType].borderColorHover};
    color: ${({ styleType }) => styleTypes[styleType].colorHover};
  }

  &:active {
    background-color: ${({ styleType }) => styleTypes[styleType].backgroundColorActive};
    ${({ icon, styleType }) =>
      !!icon && `background-image: ${makeIconUrl(styleTypes[styleType].colorActive, icon)}`};
    border-color: ${({ styleType }) => styleTypes[styleType].borderColorActive};
    box-shadow: none;
    color: ${({ styleType }) => styleTypes[styleType].colorActive};
  }

  ${({ disabled, icon }) =>
    // Base the disabled styling on the prop instead of the pseudoclass so that it works even when
    // this component is used with a non-<button> tag
    disabled &&
    css`
      && {
        background-color: ${colors.sand};
        ${!!icon && `background-image: ${makeIconUrl(colors.dust, icon)}`};
        border-color: ${colors.sand};
        box-shadow: none;
        color: ${colors.dust};
        cursor: not-allowed;
      }
    `};
`;

Button.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.oneOf(Object.keys(iconPaths)),
  size: PropTypes.oneOf(Object.keys(sizes)),
  styleType: PropTypes.oneOf(Object.keys(styleTypes)),
};

Button.defaultProps = {
  size: 'medium',
  styleType: 'secondary',
};

export default Button;
